/**
 * DateTimeDisplay Component
 *
 * Read-only formatted date/time display component with various formats
 * for different contexts (event cards, headers, activity feed, etc.)
 */

import { formatEventDate, formatEventTime, formatCompactDate, formatCompactTime, formatDateRange, getRelativeTime } from '../../lib/datetime';
import { useChatTimeBucket } from '../../contexts/ChatTimeContext';

interface DateTimeDisplayProps {
  timestamp: number;
  endTimestamp?: number;
  format?: 'full' | 'compact' | 'time-only' | 'date-only' | 'relative' | 'range';
  className?: string;
  showRelative?: boolean;
}

export function DateTimeDisplay({
  timestamp,
  endTimestamp,
  format = 'compact',
  className = '',
  showRelative = false
}: DateTimeDisplayProps) {
  const formatDateTime = () => {
    switch (format) {
      case 'full':
        return `${formatEventDate(timestamp)} at ${formatEventTime(timestamp)}`;
      
      case 'compact':
        return `${formatCompactDate(timestamp)} at ${formatCompactTime(timestamp)}`;
      
      case 'time-only':
        return formatEventTime(timestamp);
      
      case 'date-only':
        return formatEventDate(timestamp);
      
      case 'relative':
        return getRelativeTime(timestamp);
      
      case 'range':
        return formatDateRange(timestamp, endTimestamp);
      
      default:
        return formatCompactDate(timestamp);
    }
  };

  const relativeTime = showRelative ? getRelativeTime(timestamp) : null;

  return (
    <time 
      dateTime={new Date(timestamp * 1000).toISOString()}
      className={`text-text-secondary ${className}`}
      title={`${formatEventDate(timestamp)} at ${formatEventTime(timestamp)}`}
    >
      {formatDateTime()}
      {relativeTime && (
        <span className="text-text-muted text-sm ml-2">
          ({relativeTime})
        </span>
      )}
    </time>
  );
}

/**
 * Specialized variants for common use cases
 */

// For event cards - compact with relative time
export function EventDateTime({ timestamp, endTimestamp, className }: Omit<DateTimeDisplayProps, 'format'>) {
  return (
    <DateTimeDisplay 
      timestamp={timestamp}
      endTimestamp={endTimestamp}
      format="range"
      showRelative
      className={className}
    />
  );
}

// For activity feed - just relative time
export function ActivityDateTime({ timestamp, className }: Omit<DateTimeDisplayProps, 'format' | 'endTimestamp'>) {
  return (
    <DateTimeDisplay 
      timestamp={timestamp}
      format="relative"
      className={`text-text-muted text-sm ${className}`}
    />
  );
}

// For chat messages - just time
export function ChatDateTime({ timestamp, className }: Omit<DateTimeDisplayProps, 'format' | 'endTimestamp'>) {
  return (
    <DateTimeDisplay 
      timestamp={timestamp}
      format="time-only"
      className={`text-text-muted text-xs ${className}`}
    />
  );
}

// For event headers - full format
export function EventHeaderDateTime({ timestamp, endTimestamp, className }: Omit<DateTimeDisplayProps, 'format'>) {
  return (
    <DateTimeDisplay
      timestamp={timestamp}
      endTimestamp={endTimestamp}
      format="full"
      className={`text-text-primary font-medium ${className}`}
    />
  );
}

/**
 * Self-updating relative time for chat messages.
 *
 * Uses ChatTimeContext to subscribe to time updates via a shared interval.
 * Only this component re-renders on tick, not the parent ChatMessage.
 *
 * Issue #769 - Performance fix for ClubChatPage forceUpdate anti-pattern
 *
 * MUST be used inside a ChatTimeProvider (wrap at chat page level).
 */
export function ChatRelativeTime({
  timestamp,
  className = '',
}: {
  timestamp: number;
  className?: string;
}) {
  // Subscribe to bucket updates - triggers re-render every 10s
  // Using the bucket as a dependency ensures we recalculate relative time
  useChatTimeBucket();

  // Compute relative time on each render (cheap operation)
  const relativeTime = getRelativeTimeForChat(timestamp);

  return (
    <time
      dateTime={new Date(timestamp * 1000).toISOString()}
      className={`text-text-muted ${className}`}
      title={`${formatEventDate(timestamp)} at ${formatEventTime(timestamp)}`}
    >
      {relativeTime}
    </time>
  );
}

/**
 * Chat-specific relative time formatting.
 * More granular than event relative time (shows minutes for recent messages).
 */
function getRelativeTimeForChat(timestamp: number): string {
  const now = Math.floor(Date.now() / 1000);
  const diff = now - timestamp;

  if (diff < 60) return 'just now';
  if (diff < 3600) {
    const mins = Math.floor(diff / 60);
    return `${mins} ${mins === 1 ? 'minute' : 'minutes'} ago`;
  }
  if (diff < 86400) {
    const hours = Math.floor(diff / 3600);
    return `${hours} ${hours === 1 ? 'hour' : 'hours'} ago`;
  }
  if (diff < 604800) {
    const days = Math.floor(diff / 86400);
    return `${days} ${days === 1 ? 'day' : 'days'} ago`;
  }

  // For older messages, show the date
  return new Date(timestamp * 1000).toLocaleDateString();
}